/**
 * Which bill the allocation table should be showing, and how to go and get it.
 *
 * Its own module because the choice is the whole of it, and it is a choice between two questions
 * that look interchangeable and are not. An allocation records the bill it was made from, in
 * `sourceBillId`. Asking the service which bill belongs to this cycle, property and utility is a
 * different question, and it answers newest-first — right when recovering an upload somebody left
 * unfinished, wrong once an allocation exists.
 *
 * Where a utility has two bills in one cycle — the first allocated, a second uploaded afterwards and
 * not allocated — the two questions name different bills, and the table rendered the second one's
 * document and extracted values beside the first one's allocation numbers, with nothing on screen to
 * say they came from different bills (LB-5898).
 *
 * So when the allocation names its bill, that name is used and nothing is guessed.
 */
export const ALLOCATION_BILL_LOOKUP = {
  /** Nothing to fetch. */
  none: 'none',
  /** Fetch the bill this allocation was made from, by id. */
  bySourceBillId: 'bySourceBillId',
  /** Ask which bill was uploaded for this cycle, property and utility. */
  forCycle: 'forCycle',
  /** Let go of a bill this screen went and found: the allocation turned out to have a document of its own. */
  release: 'release'
};

/**
 * @param heldBillId           the bill already on screen, when there is one
 * @param heldBillWasRecovered whether that bill was brought back, rather than uploaded here just now
 * @param hasUnfinishedReplacement  whether a replacement has been started and not yet arrived
 * @param sourceBillId         the bill this allocation was made from, once it has been allocated
 * @param missingSourceBillId  a source bill already looked for on this screen and not found
 * @param attachedFileId       the file attached to this allocation, when it has one
 * @param allocationBillId     the allocation on screen, when there is one
 * @param releasedAllocationBillId an allocation made from a bill that was removed here, whose bill was let go of
 * @param billingCycleId       the cycle on screen
 * @param propertyId           the property on screen
 * @param utilityId            the utility on screen
 */
export function resolveAllocationBillLookup() {
  let _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    heldBillId = _ref.heldBillId,
    heldBillWasRecovered = _ref.heldBillWasRecovered,
    hasUnfinishedReplacement = _ref.hasUnfinishedReplacement,
    sourceBillId = _ref.sourceBillId,
    missingSourceBillId = _ref.missingSourceBillId,
    attachedFileId = _ref.attachedFileId,
    allocationBillId = _ref.allocationBillId,
    releasedAllocationBillId = _ref.releasedAllocationBillId,
    billingCycleId = _ref.billingCycleId,
    propertyId = _ref.propertyId,
    utilityId = _ref.utilityId;
  // Nothing is fetched while a replacement is outstanding. The screen lets go of the bill being
  // replaced before the new one exists, so there is a gap in which the allocation still names the
  // old bill -- and filling it would put the old extraction back on screen beside the file that is
  // replacing it.
  //
  // Outstanding, not in flight: a replacement that failed has left the new file on screen with no
  // extraction of its own, and that gap is just as wrong to fill. It reopens on the next reset,
  // which is every open, close and step to another utility.
  //
  // Asked first because it is about what the person is in the middle of doing, which outranks
  // anything the stored state currently says.
  if (hasUnfinishedReplacement) return ALLOCATION_BILL_LOOKUP.none;

  // Asked first, and deliberately: a bill the person uploaded on this screen is theirs. It is what
  // they are meant to be looking at even while the allocation still names the bill it replaces, so
  // nothing is ever fetched over the top of it.
  if (heldBillId && !heldBillWasRecovered) return ALLOCATION_BILL_LOOKUP.none;

  // The allocation names its bill, so there is nothing left to guess.
  //
  // Asked even when a bill is already held, which is not redundant. The allocation table arrives
  // after this modal has opened, so the first pass runs with no `sourceBillId` to prefer and falls
  // through to the cycle lookup below; if that answers first it leaves the newest bill on screen.
  // Were a held bill enough to stop here, the `sourceBillId` arriving moments later would find one
  // and leave the wrong document sitting beside the allocation -- the very thing this module is for.
  //
  // There is deliberately no fallback to the cycle lookup when this bill cannot be fetched: an
  // allocation whose bill has since been deleted has no document, and putting some other bill's
  // document in its place is what went wrong.
  if (sourceBillId) {
    // The allocation just removed here, still in the store until the table's reload lands (LB-6028). Its bill was let go
    // of on purpose, and fetching it would put it straight back. That allocation, not its bill: one made from the same
    // bill again since is a different allocation, and its bill is fetched as ever.
    if (allocationBillId && allocationBillId === releasedAllocationBillId) return ALLOCATION_BILL_LOOKUP.none;

    // Asked for once on this screen and not there. Releasing the bill that was on screen is what
    // makes this run again, so without remembering the answer the next pass would ask for the same
    // bill a second time and get the same nothing. It settles either way -- the second release
    // changes no dependency, so there is no third pass -- but one pointless request is one too
    // many, and a rule that stops is better than one that happens to run out of road.
    if (missingSourceBillId && sourceBillId === missingSourceBillId) return ALLOCATION_BILL_LOOKUP.none;
    return heldBillId === sourceBillId ? ALLOCATION_BILL_LOOKUP.none : ALLOCATION_BILL_LOOKUP.bySourceBillId;
  }

  // A file of the allocation's own with no bill named behind it: one that was not read, and whose details were
  // entered by hand. The newest bill for the cycle is not its bill. After a replacement it is very likely the very
  // bill this file replaced (LB-5898), and its figures would be applied over the ones entered for this file. A bill
  // already found by an earlier pass, before the allocation arrived to say so, is let go of for the same reason.
  if (attachedFileId) {
    return heldBillId && heldBillWasRecovered ? ALLOCATION_BILL_LOOKUP.release : ALLOCATION_BILL_LOOKUP.none;
  }

  // Nothing has been allocated, so nothing names a bill.
  if (heldBillId) return ALLOCATION_BILL_LOOKUP.none;

  // An allocation made from a bill was removed here (LB-6028): the table is for another bill now -- uploaded, chosen or
  // typed in -- and the cycle's newest is very likely the one just let go of, until bill-automation passes over it.
  // Reopened, the screen asks again.
  if (releasedAllocationBillId) return ALLOCATION_BILL_LOOKUP.none;

  // Recovering the most recent upload for the cycle is the right answer here, and is what this
  // screen has always done.
  if (billingCycleId && propertyId && utilityId) return ALLOCATION_BILL_LOOKUP.forCycle;
  return ALLOCATION_BILL_LOOKUP.none;
}

/**
 * Whether a lookup that came back with nothing means letting go of the bill on screen.
 *
 * Empty is the ordinary answer for a cycle nobody has uploaded to, and it changes nothing: there
 * was no bill and there still is not.
 *
 * It is not ordinary for a bill the allocation named. Going by id is what this screen does when it
 * is already holding a bill it knows is the WRONG one, so coming back empty and keeping that bill
 * is a fallback to the newest cycle bill by another route -- the exact thing the no-fallback rule
 * above exists to prevent.
 *
 * Why the lookup came back empty does not change the answer. Deleted, or simply unreachable, what
 * is held is known not to be the bill this allocation was made from, and showing it beside these
 * numbers would be saying something untrue either way.
 *
 * @param lookup               which lookup was made
 * @param heldBillWasRecovered whether the bill on screen was brought back rather than uploaded here
 */
export function shouldReleaseHeldBill() {
  let _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    lookup = _ref2.lookup,
    heldBillWasRecovered = _ref2.heldBillWasRecovered;
  // Only ever a bill this screen went and found. One the person uploaded is theirs, and never
  // reaches a by-id lookup in the first place.
  return lookup === ALLOCATION_BILL_LOOKUP.bySourceBillId && !!heldBillWasRecovered;
}